// 值班管理的「格子」定义：五类值班 × 各自的班次 × 每格人数规则。前后端共用同一份 ——
// 前端渲染的格子、后端校验/生成/统计的格子必须是同一套，否则会出现"界面上有格子但存不进去"。
//
// 业务口径（值班分五类，每类的每个分类的人数规则不同）：
//   日常排班：002A岗 / 002B岗 / 001A岗 / 001B岗 / 001C岗 五个组 × 早班 / 中班 / 晚班 —— 每格**恰好 1 人**
//   策略值班：白班（不分组）                                —— 每格**恰好 1 人**
//   普通值班：白班 / 早班 / 中班（不分组）                  —— 每格 0 ~ 1 人，一天至少 1 人（白班，或早/中两人）
//   香港值班：早班 / 中班 / 晚班（不分组）                  —— 每格 1 ~ 2 人
//   安全风控：白班 / 早班 / 中班（不分组，和普通值班一样）   —— 每格 0 ~ 1 人（可以没人）
// 合计每天 24 个格子，其中必填（每格最少 1 人）19 格 → 一天最少 19 个人上班
// （普通值班、安全风控的格子 min=0：普通值班是"白班一个人，或者早/中两个人"二选一，用 min 1 会误报缺岗）。
import { beijingDayKey, dayKeyDiff, dayKeyOfStamp as stampDayKey, daysInMonth, parseDayStamp } from "./time.js";
export const DUTY_DEPARTMENT = "套利";
export const DUTY_CATEGORIES = ["日常排班", "策略值班", "普通值班", "香港值班", "安全风控"];
/**
 * 分类的「显示名」：数据里的 category 值不变（库里、接口里仍然是 "普通值班"），
 * 只有面向人的文案（表头、弹框、通知、日志、图片、缺岗/超员提示）走这一个映射。
 * 前后端共用同一份 —— 否则会出现"界面叫深圳值班、通知里叫普通值班"。
 */
export const DUTY_CATEGORY_LABELS = { 普通值班: "深圳值班" };
/** 取分类的显示名（没配映射的分类原样返回，如 日常排班 / 策略值班 / 香港值班 / 安全风控） */
export function dutyCategoryLabel(category) {
    return DUTY_CATEGORY_LABELS[category] ?? category;
}
/**
 * 把一段已经拼好的展示文案里的类别名换成显示名。
 * 只给「整句文案」用（后端返回的 label / message、手写的说明文字）；接口字段本身不要这么改。
 */
export function dutyTextLabel(text) {
    return Object.entries(DUTY_CATEGORY_LABELS).reduce((acc, [from, to]) => acc.split(from).join(to), text);
}
/** 日常排班的五个组（按新排班表：002A岗 / 002B岗 / 001A岗 / 001B岗 / 001C岗） */
export const DUTY_ROSTER_GROUPS = ["002A岗", "002B岗", "001A岗", "001B岗", "001C岗"];
export const DUTY_SHIFTS = ["白班", "早班", "中班", "晚班"];
/** 班次时段（展示用；晚班跨天，22:00 上班次日 06:00 下班） */
export const DUTY_SHIFT_HOURS = {
    白班: "09:00~17:00",
    早班: "06:00~14:00",
    中班: "14:00~22:00",
    晚班: "22:00~06:00"
};
/**
 * 按**时刻**判断属于哪个班次（06:00~14:00 早班、14:00~22:00 中班、22:00~06:00 晚班）。
 *
 * 边界归属「刚开始的那个班」：06:00 → 早班、14:00 → 中班、22:00 → 晚班
 *（需求原话：「如果是在 06:00，14:00，22:00 发送，就直接到下一个班」）。
 * 例：08:00 早班、11:00 早班、23:00 晚班、05:00 晚班。
 *
 * @param hour 0~23 的整点（分钟不影响归属，因为边界都在整点）
 */
export function dutyShiftOfHour(hour) {
    const value = Math.floor(Number(hour));
    if (!Number.isFinite(value))
        return "晚班";
    if (value >= 6 && value < 14)
        return "早班";
    if (value >= 14 && value < 22)
        return "中班";
    return "晚班";
}
/** 'HH:mm' / 'HH:mm:ss' → 班次；解析失败返回空串（界面上就只显示日期，不硬编一个班次） */
export function dutyShiftOfClock(clock) {
    const matched = /^(\d{1,2}):(\d{2})/.exec(String(clock ?? "").trim());
    if (!matched)
        return "";
    const hour = Number(matched[1]);
    return hour >= 0 && hour <= 23 ? dutyShiftOfHour(hour) : "";
}
/** 班次简称（月历格里用）：数据里的班次值仍是「晚班」，显示成「夜」 */
export const DUTY_SHIFT_SHORT = { 白班: "白", 早班: "早", 中班: "中", 晚班: "夜" };
/**
 * 班次的「显示名」：数据里的 shift 值不变（库里、接口里仍然是 "晚班"），
 * 只有面向人的文案（当天表格、统计、缺岗/超员提示、通知、图片）走这一个映射。
 * 与 DUTY_CATEGORY_LABELS 同理 —— 前后端共用同一份，界面与通知不会一个写晚班一个写夜班。
 */
export const DUTY_SHIFT_LABELS = { 晚班: "夜班" };
/** 取班次的显示名（没配映射的班次原样返回，如 白班 / 早班 / 中班） */
export function dutyShiftLabel(shift) {
    return DUTY_SHIFT_LABELS[shift] ?? shift;
}
/**
 * 分类 × 班次的时段覆盖（只写需要覆盖的）：策略值班的白班是 09:30~17:30，
 * 与普通值班的白班（09:00~17:00）不同，所以时段要按「类别 + 班次」取，不能只看班次。
 */
export const DUTY_CATEGORY_SHIFT_HOURS = {
    策略值班: { 白班: "09:30~17:30" }
};
/** 某个格子的班次时段：先看分类覆盖，再回落到班次默认时段（前后端共用同一份口径） */
export function dutyCellHours(category, shift) {
    const override = DUTY_CATEGORY_SHIFT_HOURS[category]?.[shift];
    return override ?? DUTY_SHIFT_HOURS[shift] ?? "";
}
/** 分类的班次清单：策略值班只有白班；普通值班与安全风控是白/早/中；日常排班与香港值班是早/中/晚（界面上显示成「夜」） */
export const DUTY_CATEGORY_SHIFTS = {
    日常排班: ["早班", "中班", "晚班"],
    策略值班: ["白班"],
    普通值班: ["白班", "早班", "中班"],
    香港值班: ["早班", "中班", "晚班"],
    安全风控: ["白班", "早班", "中班"]
};
/** 一天的全部格子（24 格），顺序即界面上的展示顺序 */
export function dutyCellCatalog() {
    const cells = [];
    for (const group of DUTY_ROSTER_GROUPS) {
        for (const shift of DUTY_CATEGORY_SHIFTS["日常排班"]) {
            cells.push({ category: "日常排班", group, shift, min: 1, max: 1, rule: "恰好 1 人" });
        }
    }
    for (const shift of DUTY_CATEGORY_SHIFTS["策略值班"]) {
        cells.push({ category: "策略值班", group: "", shift, min: 1, max: 1, rule: "恰好 1 人" });
    }
    for (const shift of DUTY_CATEGORY_SHIFTS["普通值班"]) {
        // min 必须是 0：普通值班是"白班一个人，或者早/中两个人"，按天二选一，min 1 会误报缺岗
        cells.push({ category: "普通值班", group: "", shift, min: 0, max: 1, rule: "每天至少 1 人（白班，或早/中两人）" });
    }
    for (const shift of DUTY_CATEGORY_SHIFTS["香港值班"]) {
        cells.push({ category: "香港值班", group: "", shift, min: 1, max: 2, rule: "1~2 人" });
    }
    for (const shift of DUTY_CATEGORY_SHIFTS["安全风控"]) {
        cells.push({ category: "安全风控", group: "", shift, min: 0, max: 1, rule: "0~1 人（可以没人）" });
    }
    return cells;
}
/** 必填格子（最少 1 人）：15 个日常排班 + 1 个策略值班 + 3 个香港值班 = 19 */
export function dutyRequiredCells() {
    return dutyCellCatalog().filter(cell => cell.min > 0);
}
/** 一天最少需要的人数 = 必填格子数 */
export function dutyMinimumHeadcount() {
    return dutyRequiredCells().length;
}
export function dutyCellKey(cell) {
    return `${cell.category}|${cell.group}|${cell.shift}`;
}
/** 一格最多 2 人（只有香港值班有第 2 个位置），seat 是第几个位置（从 1 开始） */
export function dutySeatKey(cell, seat) {
    return `${dutyCellKey(cell)}|${seat}`;
}
export function dutyCellSame(left, right) {
    return left.category === right.category && left.group === right.group && left.shift === right.shift;
}
/** 格子标签：日常排班 002A岗 早班 / 香港值班 夜班 —— 类别与班次都走显示名（普通值班 → 深圳值班、晚班 → 夜班） */
export function dutyCellLabel(cell) {
    return `${dutyCategoryLabel(cell.category)}${cell.group ? ` ${cell.group}` : ""} ${dutyShiftLabel(cell.shift)}`;
}
/** 合并「日常排班 + 香港值班」时用的类别常量与标签前缀 */
const DUTY_ROSTER_CATEGORY = "日常排班";
const DUTY_HONG_KONG_CATEGORY = "香港值班";
export const DUTY_HONG_KONG_LABEL_PREFIX = "[香港]";
/**
 * 同一天同一班次既有一条「日常排班」、又有一条「香港值班」→ 这其实是同一个班，
 * 只是这个班在香港上。**只用于显示**（个人月度排班的逐条列表、通知文字、排班图明细行）：
 * 合成一条，标签形如 `[香港]002A岗早`，其余字段（日期 / 分组 / 班次 / 时段）取日常排班那条。
 *
 * 规则：
 *   · 按 `date + shift` 分组；
 *   · 组内**恰好**一条 日常排班 + **恰好**一条 香港值班 才合并（两条以上有歧义，原样返回）；
 *   · 输出顺序沿用入参顺序（合并后的那条落在日常排班那条的位置，其余行原样）；
 *   · 不动库里的 duty_slots，也不影响统计里的天数 / 班次计数 —— 这里只管"给人看的那一行"。
 */
export function mergeHongKongDuty(rows) {
    const list = Array.isArray(rows) ? rows : [];
    const groups = new Map();
    for (const row of list) {
        const key = `${row.date}|${row.shift}`;
        const bucket = groups.get(key);
        if (bucket)
            bucket.push(row);
        else
            groups.set(key, [row]);
    }
    /** 合并掉的香港值班行（不再单独出现） + 需要换 label 的日常排班行 */
    const dropped = new Set();
    const labels = new Map();
    for (const group of groups.values()) {
        const roster = group.filter(row => row.category === DUTY_ROSTER_CATEGORY);
        const hongKong = group.filter(row => row.category === DUTY_HONG_KONG_CATEGORY);
        if (roster.length !== 1 || hongKong.length !== 1)
            continue;
        const cell = roster[0];
        dropped.add(hongKong[0]);
        labels.set(cell, `${DUTY_HONG_KONG_LABEL_PREFIX}${cell.group}${DUTY_SHIFT_SHORT[cell.shift] ?? cell.shift}`);
    }
    return list
        .filter(row => !dropped.has(row))
        .map(row => {
        const label = labels.get(row);
        return label === undefined ? row : { ...row, label };
    });
}
/**
 * 连续日期上**同一个班**（同标签：同类别 + 同分组 + 同班次）合并成一条，日期显示成一个区间
 * （例如方苏 10-24 ~ 10-27 都是「日常排班 001C岗 中班」→ 一行 `10-24~10-27`）。
 *
 * 只在「显示」这一层用（个人月度统计的逐条列表、通知文字、排班图明细行），
 * 不动 duty_slots、不影响天数 / 班次计数。结果按起始日期排序，同一天内按班次名。
 * 入参一般先过 mergeHongKongDuty，这样被合并成 `[香港]002A岗早` 的日期也能连成一段。
 */
export function mergeDutyRuns(rows) {
    const list = Array.isArray(rows) ? rows : [];
    /** 同一个"班"（标签相同）的所有行 */
    const byShift = new Map();
    for (const row of list) {
        const key = row.label ?? dutyCellLabel(row);
        byShift.set(key, [...(byShift.get(key) ?? []), row]);
    }
    const runs = [];
    for (const [label, group] of byShift) {
        const sorted = [...group].sort((a, b) => a.date.localeCompare(b.date));
        let current = [];
        for (const row of sorted) {
            const last = current.at(-1);
            if (last && dutyNextDay(last.date) === row.date)
                current.push(row);
            else {
                if (current.length)
                    runs.push({ label, rows: current });
                current = [row];
            }
        }
        if (current.length)
            runs.push({ label, rows: current });
    }
    return runs
        .map(({ label, rows: run }) => {
        const first = run[0];
        const last = run.at(-1);
        return { ...first, from: first.date, to: last.date, days: run.length, label };
    })
        .sort((a, b) => a.from.localeCompare(b.from) || a.label.localeCompare(b.label));
}
/**
 * 在合并好的排班行之间补上**休息**行：上一段结束到下一段开始之间的空档显示成「休息」
 * （例如方苏 10-27 结束、10-29 又开始 → 中间 10-28 一行「休息」）。
 *
 * 只在「显示」这一层用（个人月度统计、通知文字、排班图明细行）；不动 duty_slots、不减天数统计。
 * 只补**两段排班之间**的空档：本月第一段排班之前、最后一段之后的日期不算（那段时间人还没开始/已经结束排班）。
 */
export function withDutyRestDays(runs) {
    const list = Array.isArray(runs) ? runs : [];
    const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
    const out = [];
    /** 已经覆盖到的最晚日期：同一天可能有好几行（不同班次），空档要和"最晚的那天"比 */
    let coveredTo = "";
    for (const row of sorted) {
        if (coveredTo && row.date > coveredTo) {
            const gapDays = dutyDayDiff(coveredTo, row.date) - 1;
            if (gapDays > 0) {
                const restFrom = dutyNextDay(coveredTo);
                const restTo = dutyPrevDay(row.date);
                out.push({
                    ...row,
                    rest: true,
                    label: "休息",
                    date: restFrom,
                    from: restFrom,
                    to: restTo,
                    days: gapDays
                });
            }
        }
        out.push(row);
        if (row.to > coveredTo)
            coveredTo = row.to;
    }
    return out;
}
export function dutyCellRuleOf(cell) {
    return dutyCellCatalog().find(item => dutyCellSame(item, cell));
}
/**
 * 规则引擎里的"段"类型：连续上班的一段只能是同一个类型。
 * 日常排班按班次分（早班段 / 中班段 / 晚班段）；其余值班类（策略 / 普通 / 香港 / 安全风控）
 * 都算「值班」一段（业务口径：值班是独立的一档，值班之间换分类不用先休息）。
 */
export function dutyKindOf(cell) {
    return cell.category === "日常排班" ? cell.shift : "值班";
}
/**
 * 界面上的分类说明（规则卡片 / 当天表格的分组头）。
 * category 是**数据键**（不能改，取值/校验都按它），显示成文字时请套 dutyCategoryLabel(category)。
 */
export const DUTY_CATEGORY_RULES = [
    {
        category: "日常排班",
        shifts: "早 / 中 / 夜",
        rule: "恰好 1 人",
        hint: `${DUTY_ROSTER_GROUPS.join("、")}五个组，每组的早/中/夜都必须有且只有一个人`
    },
    {
        category: "策略值班",
        shifts: "白",
        rule: "恰好 1 人",
        hint: `不分组，白班（${dutyCellHours("策略值班", "白班")}）必须有且只有一个人`
    },
    {
        category: "普通值班",
        shifts: "白 / 早 / 中",
        rule: "每天至少 1 人",
        hint: `两种排法：白班 1 个人（${dutyCellHours("普通值班", "白班")}），或者早班 + 中班各 1 个人`
            + `（${dutyCellHours("普通值班", "早班")} / ${dutyCellHours("普通值班", "中班")}，两人）；每格最多 1 人`
    },
    { category: "香港值班", shifts: "早 / 中 / 夜", rule: "1~2 人", hint: "不分组，早/中/夜都至少 1 人，最多 2 人" },
    { category: "安全风控", shifts: "白 / 早 / 中", rule: "0~1 人（可以没人）", hint: "可选，最多 1 人" }
];
// ===== 规则参数（前后端共用的"口径"）=====
export const DUTY_RULES = {
    /** 一段连续上班最多 6 天（业务口径：连续 5~6 天后必须休息） */
    maxSegmentDays: 6,
    /** 一段连续上班的建议天数下限（用于提示"排得太碎"） */
    minSegmentDays: 5,
    /** 每人每月最多上班天数（一个班算一天） */
    maxMonthDays: 20,
    /** 一段结束后的最少休息天数：日常排班晚班 3 天（建议 3~4 天），早/中班 1 天（建议 1~2 天） */
    minRestDays: { 早班: 1, 中班: 1, 晚班: 3, 值班: 1 },
    /** 值班段达到这个天数后才要求休息（不足 6 天的值班段不用休息） */
    restRequiredFrom: { 值班: 6 }
};
/** 界面上的规则说明（值班管理右上角的「排班规则」用它） */
export const DUTY_RULES_SUMMARY = [
    "一个班算一天，每人每月最多 20 天。",
    "一段连续上班必须是同一个班次，最长 6 天，休息后才能继续排。",
    "日常排班的夜班段结束后休 3~4 天；早班 / 中班段结束后休 1~2 天。",
    `值班（${DUTY_CATEGORIES.filter(category => category !== "日常排班").map(dutyCategoryLabel).join(" / ")}）连续上满 ${DUTY_RULES.restRequiredFrom.值班} 天后，也要休 1~2 天。`,
    "同一天不能排两个班。",
    "每格人数按分类规则校验：少了标「缺岗」，多了标「超员」——都只提醒，不阻断保存。"
];
// ===== 日期工具（月份一律用 YYYY-MM，日期一律用 YYYY-MM-DD）=====
// 值班业务按北京时间算「今天」和星期几，**不能依赖服务器本地时区**：
// 云服务器通常是 UTC，用 getDate()/getDay() 这类本地时间取值会把日期整体错一天
//（UTC 的 10-19 00:00+08:00 其实是 10-18T16:00Z，getDay() 得到的是周日）。
// 「北京时间」这套换算的唯一真源在 ./time.js，这里不再自己实现一份，只做薄封装。
const pad = (value) => String(value).padStart(2, "0");
/** Date（绝对时刻）→ 北京时间的 YYYY-MM-DD（= time.ts 的 beijingDayKey，保留旧名字给现有调用方） */
export function dutyDayKey(date) {
    return beijingDayKey(date);
}
/** 接受 'YYYY-MM' / 'YYYY-MM-DD' / ISO 时间串，统一取所属月份 */
export function dutyMonthOf(value) {
    const text = String(value ?? "").trim();
    const matched = /^(\d{4})-(\d{2})/.exec(text);
    if (matched)
        return `${matched[1]}-${matched[2]}`;
    const stamp = Date.parse(text);
    return Number.isFinite(stamp) ? dutyDayKey(new Date(stamp)).slice(0, 7) : "";
}
export function isValidDutyMonth(value) {
    const month = dutyMonthOf(value);
    if (!month)
        return false;
    const monthNumber = Number(month.slice(5, 7));
    return monthNumber >= 1 && monthNumber <= 12;
}
export function isValidDutyDay(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value ?? "").trim());
}
/** 月份里的全部日期（含周末，值班每天都有） */
export function dutyMonthDays(month) {
    const year = Number(month.slice(0, 4));
    const monthNumber = Number(month.slice(5, 7));
    if (!Number.isInteger(year) || !Number.isInteger(monthNumber))
        return [];
    const days = daysInMonth(year, monthNumber);
    return Array.from({ length: days }, (_, index) => `${month}-${pad(index + 1)}`);
}
/** 月份前后各 n 天（跨月段与跨月休息判定要带上邻近月份的排班） */
export function dutyDateWindow(month, beforeDays = 10, afterDays = 10) {
    const days = dutyMonthDays(month);
    const first = `${month}-01`;
    const last = days.at(-1) ?? first;
    return { from: dutyAddDays(first, -beforeDays), to: dutyAddDays(last, afterDays) };
}
export function dutyMonthLabel(month) {
    return `${month.slice(0, 4)} 年 ${Number(month.slice(5, 7))} 月`;
}
/** 上一个月 / 下一个月（纯数字运算，跨年由算术处理，不受时区影响） */
export function dutyShiftMonth(month, delta) {
    const year = Number(month.slice(0, 4));
    const monthNumber = Number(month.slice(5, 7));
    if (!Number.isInteger(year) || !Number.isInteger(monthNumber))
        return "";
    const index = year * 12 + (monthNumber - 1) + delta;
    return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`;
}
/** 日期差（b - a，单位天）；非法输入返回 NaN */
export function dutyDayDiff(a, b) {
    return dayKeyDiff(a, b);
}
export function dutyNextDay(date) {
    return dutyAddDays(date, 1);
}
export function dutyPrevDay(date) {
    return dutyAddDays(date, -1);
}
/** 日期加 n 天（n 可为负）：按"日期本身"算，与时区无关 */
export function dutyAddDays(date, days) {
    const stamp = parseDayStamp(date);
    return Number.isFinite(stamp) ? stampDayKey(stamp + days * 86_400_000) : "";
}
/**
 * 星期几（一~日），月历表头、列表模式、当天值班表与 TG 图片都用它。
 * 用 UTC 取星期：2026-10-19 建的是 UTC 的 10-19，getUTCDay() 一定是周一，
 * 不受服务器时区影响（以前用 getDay()，UTC 服务器上整体错一天）。
 */
export function dutyWeekday(date) {
    const stamp = parseDayStamp(date);
    if (!Number.isFinite(stamp))
        return "";
    return ["日", "一", "二", "三", "四", "五", "六"][new Date(stamp).getUTCDay()] ?? "";
}
